<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import type { CrafterWishlistNamesQuery } from '@/__generated__/gateway/graphql';
import IconPlus from '@/components/MyHobbii/components/icons/IconPlus.vue';
import AddToWishlistPopup from '@/components/MyHobbii/views/MyWishlist/modals/CreateWishlistModal.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import IconCheck from '@/shared/components/UI/Icons/IconCheck.vue';
import IconX from '@/shared/components/UI/Icons/IconX.vue';
import LoadingSpinner from '@/shared/components/UI/LoadingSpinner.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { getTranslatedWishlistName } from '@/shared/composables/wishlist';
import { useWishlistStore } from '@/shared/pinia/modules/wishlist';
import { WishlistApiService } from '@/shared/services/wishlistAPI';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

type WishlistItem = NonNullable<
  NonNullable<
    NonNullable<CrafterWishlistNamesQuery['crafter']>['wishlists']
  >['wishlists']
>[number];

type Props = {
  active: boolean;
  productGroupIdentifier?: string;
  sku?: string;
};

const IS_DEV = import.meta.env.DEV;

const props = defineProps<Props>();

type ConfirmedPayload = {
  wishlistIds: string[];
  primaryWishlistName: string;
  totalCount: number;
  willBeInWishlist: boolean;
};

const emit = defineEmits<{
  'update:active': [value: boolean];
  confirmed: [payload: ConfirmedPayload];
}>();

const { t } = useTranslations();
const wishlistStore = useWishlistStore();

// State
const wishlists = ref<WishlistItem[]>([]);
const selectedWishlistIds = ref<Set<string>>(new Set());
const initialWishlistIds = ref<Set<string>>(new Set()); // Track which wishlists originally had the item
// Track item IDs for wishlists not in store (needed for removal since store doesn't have these items)
const itemIdsByWishlistId = ref<Map<string, string>>(new Map());
const loading = ref(false);
const showCreatePopup = ref(false);
const error = ref<string | null>(null);

// Constants for scroll behavior - show max 5 items before scrolling
const MAX_VISIBLE_ITEMS = 5;
const ITEM_HEIGHT = 52;

// Hide selector popup when create popup is open
const effectiveActive = computed(() => props.active && !showCreatePopup.value);

const defaultWishlist = computed(() =>
  wishlists.value.find((w) => w.is_default)
);
const otherWishlists = computed(() =>
  wishlists.value.filter((w) => !w.is_default)
);

// Check if there are any changes from the initial state
const hasChanges = computed(() => {
  // Check if any new wishlists were selected (to add)
  const hasAdditions = Array.from(selectedWishlistIds.value).some(
    (id) => !initialWishlistIds.value.has(id)
  );
  // Check if any existing wishlists were deselected (to remove)
  const hasRemovals = Array.from(initialWishlistIds.value).some(
    (id) => !selectedWishlistIds.value.has(id)
  );
  return hasAdditions || hasRemovals;
});

// Calculate if scrolling is needed and the max height for the scrollable area
const needsScroll = computed(() => wishlists.value.length > MAX_VISIBLE_ITEMS);
const scrollAreaMaxHeight = computed(
  () => `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT}px`
);

// Load wishlists when popup becomes active
onMounted(async () => {
  if (props.active) {
    await loadWishlists();
  }
});

watch(
  () => props.active,
  async (newActive) => {
    if (newActive) {
      await loadWishlists();
    } else {
      // Reset state when closing
      selectedWishlistIds.value = new Set();
      initialWishlistIds.value = new Set();
      itemIdsByWishlistId.value = new Map();
      error.value = null;
    }
  }
);

// Flag to prevent concurrent loadWishlists calls
let isLoadingInProgress = false;

const loadWishlists = async () => {
  // Prevent concurrent loads to avoid race conditions
  if (isLoadingInProgress) {
    return;
  }

  isLoadingInProgress = true;
  loading.value = true;
  error.value = null;
  try {
    if (!wishlistStore.dataLoaded) {
      await wishlistStore.fetchWishlist(false);
    }

    const result = await WishlistApiService.fetchWishlistNames();
    if (result) {
      wishlists.value = result;

      // Check which wishlists already contain this item
      const existingWishlistIds = new Set<string>();
      // Track item IDs for all wishlists (needed for removal operations)
      const discoveredItemIds = new Map<string, string>();

      // Check against the store's loaded items
      // The store now fetches ALL wishlists, so this covers everything
      for (const wishlist of result) {
        if (
          wishlistStore.isInSpecificWishlist(
            wishlist.id,
            props.productGroupIdentifier,
            props.sku
          )
        ) {
          existingWishlistIds.add(wishlist.id);
          // Get the item ID from the store for later use in removal
          const storeItem = wishlistStore.getWishlistItemFromWishlist(
            wishlist.id,
            props.productGroupIdentifier,
            props.sku
          );
          if (storeItem) {
            discoveredItemIds.set(wishlist.id, storeItem.id);
          }
        }
      }

      // Set initial state and pre-select wishlists that already have the item
      initialWishlistIds.value = existingWishlistIds;
      selectedWishlistIds.value = new Set(existingWishlistIds);
      itemIdsByWishlistId.value = discoveredItemIds;
    }
  } catch (err) {
    // Report to Bugsnag for production error tracking
    notifyBugsnagWithContext('WishlistSelectorModal', err, {
      message: 'Failed to load wishlists',
      data: {
        productGroupIdentifier: props.productGroupIdentifier,
        sku: props.sku
      }
    });

    if (IS_DEV) {
      console.error('[WishlistSelectorModal] Failed to load wishlists:', err);
    }
    error.value = t('account.wishlist.selection.error_loading');
  } finally {
    loading.value = false;
    isLoadingInProgress = false;
  }
};

const toggleWishlist = (wishlistId: string) => {
  const newSet = new Set(selectedWishlistIds.value);
  if (newSet.has(wishlistId)) {
    newSet.delete(wishlistId);
  } else {
    newSet.add(wishlistId);
  }
  selectedWishlistIds.value = newSet;
};

const isSelected = (wishlistId: string) =>
  selectedWishlistIds.value.has(wishlistId);

const handleConfirm = async () => {
  if (!hasChanges.value) {
    close();
    return;
  }

  const selectedIds = Array.from(selectedWishlistIds.value);
  const initialIds = Array.from(initialWishlistIds.value);

  // Find wishlists to add (selected but not in initial)
  const toAdd = selectedIds.filter((id) => !initialWishlistIds.value.has(id));
  // Find wishlists to remove (in initial but not selected)
  const toRemove = initialIds.filter(
    (id) => !selectedWishlistIds.value.has(id)
  );

  // Calculate final state before operations - item will be in wishlist if any wishlists remain selected
  const willBeInWishlist = selectedWishlistIds.value.size > 0;

  // Emit confirmed event immediately for responsive UI
  if (toAdd.length > 0) {
    const firstSelectedId = toAdd[0];
    const firstWishlist = wishlists.value.find((w) => w.id === firstSelectedId);
    const primaryWishlistName = firstWishlist?.name ?? '';

    emit('confirmed', {
      wishlistIds: toAdd,
      primaryWishlistName,
      totalCount: toAdd.length,
      willBeInWishlist
    });
  } else {
    emit('confirmed', {
      wishlistIds: [],
      primaryWishlistName: '',
      totalCount: 0,
      willBeInWishlist
    });
  }

  // Close popup immediately for better UX - operations continue in background
  close();

  // Execute all add operations in parallel for better performance
  // Each operation is to a different wishlist, so no race conditions
  const addPromises = toAdd.map(async (wishlistId) => {
    try {
      await wishlistStore.addToWishlist(
        wishlistId,
        props.productGroupIdentifier,
        props.sku
      );
    } catch (err) {
      // Report to Bugsnag for production error tracking
      notifyBugsnagWithContext('WishlistSelectorModal', err, {
        message: 'Failed to add item to wishlist',
        data: {
          wishlistId,
          productGroupIdentifier: props.productGroupIdentifier,
          sku: props.sku
        }
      });

      if (IS_DEV) {
        console.error(
          `[WishlistSelectorModal] Failed to add to wishlist ${wishlistId}:`,
          err
        );
      }
      // The optimistic updates in the store will handle rollback on failure
    }
  });

  // Execute all remove operations in parallel for better performance
  const removePromises = toRemove.map(async (wishlistId) => {
    try {
      // First try to get item ID from our tracked items (includes wishlists not in store)
      let itemId = itemIdsByWishlistId.value.get(wishlistId);

      // Fallback to store lookup for items that are in the store
      if (!itemId) {
        const storeItem = wishlistStore.getWishlistItemFromWishlist(
          wishlistId,
          props.productGroupIdentifier,
          props.sku
        );
        itemId = storeItem?.id;
      }

      if (itemId) {
        await wishlistStore.removeFromWishlist(wishlistId, itemId);
      } else {
        // Report to Bugsnag - this is an unexpected state where we tried to remove
        // an item but couldn't find its ID in either the API response or the store
        notifyBugsnagWithContext(
          'WishlistSelectorModal',
          'Could not find item ID for removal',
          {
            message: 'Item ID not found when attempting wishlist removal',
            data: {
              wishlistId,
              productGroupIdentifier: props.productGroupIdentifier,
              sku: props.sku
            }
          }
        );

        if (IS_DEV) {
          console.warn(
            `[WishlistSelectorModal] Could not find item ID for wishlist ${wishlistId}`
          );
        }
      }
    } catch (err) {
      // Report to Bugsnag for production error tracking
      notifyBugsnagWithContext('WishlistSelectorModal', err, {
        message: 'Failed to remove item from wishlist',
        data: {
          wishlistId,
          productGroupIdentifier: props.productGroupIdentifier,
          sku: props.sku
        }
      });

      if (IS_DEV) {
        console.error(
          `[WishlistSelectorModal] Failed to remove from wishlist ${wishlistId}:`,
          err
        );
      }
      // The optimistic updates in the store will handle rollback on failure
    }
  });

  // Wait for all operations to complete (success or failure)
  await Promise.allSettled([...addPromises, ...removePromises]);
};

const handleCreateWishlist = async (wishlistName: string) => {
  // Step 1: Create the wishlist - if this fails, re-throw to let CreateWishlistModal handle it
  let newWishlist;
  try {
    newWishlist = await wishlistStore.createWishlist(wishlistName);
  } catch (err) {
    // Note: Error is reported to Bugsnag by CreateWishlistModal when it catches the re-thrown error
    if (IS_DEV) {
      console.error('[WishlistSelectorModal] Failed to create wishlist:', err);
    }
    // Re-throw to let CreateWishlistModal handle error display and Bugsnag reporting
    throw err;
  }

  if (!newWishlist) {
    return;
  }

  // Step 2: Wishlist created successfully - emit confirmation and close popups
  // We do this BEFORE adding the item so the user sees success even if add fails
  emit('confirmed', {
    wishlistIds: [newWishlist.id],
    primaryWishlistName: wishlistName,
    totalCount: 1,
    willBeInWishlist: true
  });

  // Close both popups - the wishlist exists, user can add items later if needed
  showCreatePopup.value = false;
  close();

  // Step 3: Add the item to the newly created wishlist (best-effort, non-blocking)
  // If this fails, the wishlist was still created successfully and we've already
  // closed the popups. The store's optimistic updates will handle any rollback.
  try {
    await wishlistStore.addToWishlist(
      newWishlist.id,
      props.productGroupIdentifier,
      props.sku
    );
  } catch (err) {
    // Report to Bugsnag but don't re-throw - the wishlist was created successfully
    notifyBugsnagWithContext('WishlistSelectorModal', err, {
      message: 'Failed to add item to newly created wishlist',
      data: {
        wishlistId: newWishlist.id,
        wishlistName,
        productGroupIdentifier: props.productGroupIdentifier,
        sku: props.sku
      }
    });

    if (IS_DEV) {
      console.error(
        '[WishlistSelectorModal] Failed to add item to new wishlist:',
        err
      );
    }
    // Note: We intentionally don't re-throw here. The wishlist was created,
    // so showing "name already taken" on retry would be confusing.
    // The user can manually add the item to the wishlist later.
  }
};

const close = () => {
  emit('update:active', false);
};
</script>

<template>
  <Modal
    :active="effectiveActive"
    size="sm"
    :close-button="false"
    @update:active="emit('update:active', $event)"
  >
    <template #header>
      <div class="flex w-full items-center justify-between">
        <h2 class="text-xl font-bold text-gray-900">
          {{ t('account.wishlist.selection.title') }}
        </h2>
        <button
          class="flex size-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          :aria-label="t('global.close')"
          @click="close"
        >
          <IconX class="size-5" />
        </button>
      </div>
    </template>

    <template #body>
      <div class="relative flex flex-col">
        <LoadingSpinner :show="loading">
          {{ t('account.wishlist.text_loading') }}
        </LoadingSpinner>

        <div
          v-if="error"
          class="mb-4 rounded-md bg-error-50 p-3 text-sm text-error-600"
        >
          {{ error }}
        </div>

        <div v-if="!loading" class="flex flex-col">
          <!-- Scrollable Wishlists Container -->
          <div
            class="hb-wishlist-scroll"
            :class="{ 'overflow-y-auto pr-3': needsScroll }"
            :style="needsScroll ? { maxHeight: scrollAreaMaxHeight } : {}"
          >
            <!-- Default Wishlist -->
            <div v-if="defaultWishlist" class="mb-4">
              <button
                class="grid w-full grid-cols-[1fr_24px] items-center gap-3 rounded-lg py-3 text-left transition-colors hover:bg-gray-50"
                @click="toggleWishlist(defaultWishlist.id)"
              >
                <span
                  class="truncate text-gray-900"
                  :class="
                    isSelected(defaultWishlist.id) ? 'font-bold' : 'font-medium'
                  "
                >
                  {{ getTranslatedWishlistName(defaultWishlist, t) }}
                </span>
                <span
                  class="flex size-6 items-center justify-center rounded border-2 transition-all"
                  :class="[
                    isSelected(defaultWishlist.id)
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-300 bg-white'
                  ]"
                >
                  <Transition name="fade">
                    <IconCheck
                      v-if="isSelected(defaultWishlist.id)"
                      class="size-4"
                    />
                  </Transition>
                </span>
              </button>
            </div>

            <!-- Other Wishlists Section -->
            <div v-if="otherWishlists.length > 0">
              <hr class="mb-4 border-gray-200" />
              <h3 class="mb-3 text-sm font-bold text-gray-900">
                {{ t('account.wishlist.selection.add_to_other') }}
              </h3>
              <div class="flex flex-col gap-1">
                <button
                  v-for="wishlist in otherWishlists"
                  :key="wishlist.id"
                  class="grid w-full grid-cols-[1fr_24px] items-center gap-3 rounded-lg py-3 text-left transition-colors hover:bg-gray-50"
                  @click="toggleWishlist(wishlist.id)"
                >
                  <span
                    class="truncate text-gray-900"
                    :class="{ 'font-bold': isSelected(wishlist.id) }"
                  >
                    {{ getTranslatedWishlistName(wishlist, t) }}
                  </span>
                  <span
                    class="flex size-6 items-center justify-center rounded border-2 transition-all"
                    :class="[
                      isSelected(wishlist.id)
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white'
                    ]"
                  >
                    <Transition name="fade">
                      <IconCheck
                        v-if="isSelected(wishlist.id)"
                        class="size-4"
                      />
                    </Transition>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Create New List Button -->
          <button
            class="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-3 font-medium text-gray-700 transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary"
            @click="showCreatePopup = true"
          >
            {{ t('account.wishlist.selection.create_new') }}
            <IconPlus class="size-5" />
          </button>
        </div>
      </div>
    </template>

    <template #footer>
      <ButtonText
        theme="primary"
        :label="t('account.wishlist.selection.confirm')"
        :disabled="!hasChanges"
        full-width
        @click="handleConfirm"
      />
    </template>
  </Modal>

  <!-- Create New Wishlist Popup -->
  <AddToWishlistPopup
    :active="showCreatePopup"
    :on-create="handleCreateWishlist"
    @update:active="showCreatePopup = $event"
  />
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar for wishlist container */
.hb-wishlist-scroll {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.hb-wishlist-scroll::-webkit-scrollbar {
  width: 6px;
}

.hb-wishlist-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.hb-wishlist-scroll::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

.hb-wishlist-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
